import { useEffect, useState } from "react";
import {
  deleteInvoice,
  getInvoicesByArtist,
  updateInvoice,
} from "../../../api/invoicesApi";
import PageLoader from "../../../components/pageLoader";
import Toast from "../../../components/toast";
import ToastContainer from "../../../components/toastContainer";
import {
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { updateArtist } from "../../../api/artistApi";
import ArtistCompanyFormModal from "../ArtistCompanyFormModal";
import DeleteConfirmationModal from "../../../components/deleteModal";
import InvoiceFormModal from "../InvoiceFormModal";
import { getBookingsByArtist } from "../../../api/bookingsApi";

const FinancialTab = ({ artist, onRefreshArtist }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [activeInvoice, setActiveInvoice] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [invoicesaving, setInvoiceSaving] = useState(false);

  const [bookings, setBookings] = useState([]);

  const formatAddress = (address) => {
    if (!address) return "-";

    const parts = [
      address.address_1,
      address.address_2,
      address.address_3,
      address.city,
      address.county,
      address.country,
      address.postcode,
    ];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await getInvoicesByArtist(artist.id);
      const list = Array.isArray(data) ? data : data.results || [];
      setInvoices(list);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to load invoices",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
      try {
        setLoading(true);
        const data = await getBookingsByArtist(artist.id);
        const list = Array.isArray(data) ? data : data.results || [];
        setBookings(list);
      } catch (err) {
        setToast({
          type: "error",
          message: err?.response?.data?.detail || "Failed to load bookings",
        });
      } finally {
        setLoading(false);
      }
    };

  const handleUpdateArtist = async (formData) => {
    try {
      setSaving(true);

      const payload = {
        company: {
          name: formData.company_name,
          reg_no: formData.company_reg_no,
          vat_no: formData.company_vat_no,
          address: {
            address_1: formData.addressLine1,
            address_2: formData.addressLine2,
            address_3: formData.addressLine3,
            city: formData.city,
            county: formData.county,
            country: formData.country,
            postcode: formData.postcode,
          },
        },
      };

      await updateArtist(artist.id, payload);

      setToast({
        type: "success",
        message: "Company details updated successfully",
      });

      setShowEditModal(false);

      // refresh artist data
      onRefreshArtist(); // pass this from parent
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInvoice = async (form, setError) => {
    try {
      setSaving(true);

      await updateInvoice(activeInvoice.id, form);

      setIsInvoiceModalOpen(false);
      setActiveInvoice(null);
      fetchInvoices(); // refresh list
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (artist?.id) {
      fetchInvoices();
      fetchBookings();
    }
  }, [artist?.id]);

  if (loading) return <PageLoader />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
      {/* Left – Company Details */}
      <div className="bg-slate-900/80 border border-white/10 rounded-xl p-4 space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">Company Details</h3>
          <button
            onClick={() => setShowEditModal(true)}
            className="text-xs text-pink-500 hover:underline"
          >
            Edit
          </button>
        </div>

        <Info label="Company Name" value={artist.company?.name} />
        <Info label="Company Reg No" value={artist.company?.reg_no} />
        <Info label="VAT No" value={artist.company?.vat_no} />
        <Info
          label="Company Address"
          value={formatAddress(artist.company?.address)}
        />
      </div>

      {/* Right – Invoices */}
      <div className="lg:col-span-2 bg-slate-900/80 border border-white/10 rounded-xl p-4">
        <h3 className="text-white font-semibold mb-4">Invoices</h3>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-slate-400">
              <tr>
                <th className="text-left py-2">Invoice No</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Amount</th>
                <th className="text-center py-2">PDF</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="border-b border-white/5 hover:bg-slate-800/40 transition"
                >
                  <td className="py-2 text-white">
                    {invoice.invoice_no || invoice.id}
                  </td>

                  <td className="py-2 text-slate-400">
                    {invoice.status || "-"}
                  </td>

                  <td className="py-2 text-slate-400">
                    {invoice.amount || "-"}
                  </td>

                  <td className="py-2 text-center">
                    {invoice.pdf_file ? (
                      <button
                        onClick={() => window.open(invoice.pdf_file, "_blank")}
                        className="text-pink-500 hover:text-pink-400"
                      >
                        <DocumentTextIcon className="w-5 h-5 inline" />
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="py-2 text-right space-x-3">
                    <button
                      onClick={() => {
                        //setActiveInvoice(invoice);
                        //setIsInvoiceModalOpen(true);
                      }}
                      className="text-slate-400 hover:text-white"
                    >
                      <PencilIcon className="w-4 h-4 inline" />
                    </button>
                    <button
                      //onClick={() => setDeletingInvoice(invoice)}
                      className="text-red-400 hover:text-red-500"
                    >
                      <TrashIcon className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}

              {!invoices.length && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-400">
                    No invoices found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Left – Contracts / Gigs */}
      <div className="bg-slate-900/80 border border-white/10 rounded-xl p-4 text-sm">
        <h3 className="text-white font-semibold mb-4">Contracts & Gigs</h3>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-slate-400">
              <tr>
                <th className="text-left py-2">Contract No</th>
                <th className="text-left py-2">Promoter</th>
                <th className="text-left py-2">Gig Date</th>
              </tr>
            </thead>

            <tbody>
              {(bookings || []).map((contract) => (
                <tr
                  key={contract.id}
                  className="border-b border-white/5 hover:bg-slate-800/40 transition"
                >
                  <td className="py-2 text-white">
                    {contract.contract_no || contract.id}
                  </td>

                  <td className="py-2 text-slate-400">
                    {contract.promoter?.name || "-"}
                  </td>

                  <td className="py-2 text-slate-400">
                    {contract.date || "-"}
                  </td>
                </tr>
              ))}

              {!bookings?.length && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-slate-400">
                    No contracts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ArtistCompanyFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateArtist}
        loading={saving}
        initialData={artist}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingInvoice}
        onClose={() => setDeletingInvoice(null)}
        onConfirm={async () => {
          try {
            await deleteInvoice(deletingInvoice.id);
            setDeletingInvoice(null);
            fetchInvoices();
          } catch (err) {
            setToast({
              type: "error",
              message: err?.response?.data?.detail,
            });
          }
        }}
      />

      <InvoiceFormModal
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setActiveInvoice(null);
        }}
        onSubmit={handleUpdateInvoice}
        loading={saving}
        initialData={activeInvoice}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-slate-400">{label}</p>
    <p className="text-white">{value || "-"}</p>
  </div>
);

export default FinancialTab;
